// ---------------------------------------------------------------------------
// DSS-30: Display control — block/flex/grid/inline-block/inline/none
// ---------------------------------------------------------------------------
import { useCallback } from "react";
import {
  Square,
  Columns3,
  Grid3X3,
  AlignHorizontalJustifyStart,
  Minus,
  EyeOff,
} from "lucide-react";
import type { ReactNode } from "react";

// ── Types ──
export type DisplayType = "block" | "flex" | "grid" | "inline-block" | "inline" | "none";

// ── Constants ──
interface DisplayOption {
  readonly value: DisplayType;
  readonly label: string;
  readonly icon: ReactNode;
  readonly description: string;
}

const DISPLAY_OPTIONS: readonly DisplayOption[] = [
  {
    value: "block",
    label: "Block",
    icon: <Square className="h-4 w-4" />,
    description: "Full width, stacks vertically",
  },
  {
    value: "flex",
    label: "Flex",
    icon: <Columns3 className="h-4 w-4" />,
    description: "Flexible layout container",
  },
  {
    value: "grid",
    label: "Grid",
    icon: <Grid3X3 className="h-4 w-4" />,
    description: "Grid layout container",
  },
  {
    value: "inline-block",
    label: "Inline Block",
    icon: <AlignHorizontalJustifyStart className="h-4 w-4" />,
    description: "Inline with block sizing",
  },
  {
    value: "inline",
    label: "Inline",
    icon: <Minus className="h-4 w-4" />,
    description: "Flows with text content",
  },
  {
    value: "none",
    label: "None",
    icon: <EyeOff className="h-4 w-4" />,
    description: "Completely hidden",
  },
];

// ── Props ──
interface DisplayControlProps {
  readonly value: string;
  readonly onChange: (display: string) => void;
}

// ── Component ──
export default function DisplayControl({ value, onChange }: DisplayControlProps) {
  const currentValue = (value || "block") as DisplayType;

  const handleSelect = useCallback(
    (display: DisplayType) => {
      onChange(display);
    },
    [onChange],
  );

  const selectedOption = DISPLAY_OPTIONS.find((o) => o.value === currentValue);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Square className="h-3.5 w-3.5" />
        Display
      </div>

      {/* Visual selector */}
      <div className="grid grid-cols-3 gap-1.5">
        {DISPLAY_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option.value)}
            title={option.description}
            className={[
              "flex flex-col items-center gap-1 rounded-md border p-2 transition-colors",
              currentValue === option.value
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
                : "border-[var(--color-border)] hover:border-[var(--color-primary)]/50",
            ].join(" ")}
          >
            <span
              className={
                currentValue === option.value
                  ? "text-[var(--color-primary)]"
                  : "text-[var(--color-muted-foreground)]"
              }
            >
              {option.icon}
            </span>
            <span className="text-[9px] font-medium text-[var(--color-foreground)]">
              {option.label}
            </span>
          </button>
        ))}
      </div>

      {/* Description */}
      {selectedOption && (
        <p className="text-[10px] text-[var(--color-muted-foreground)]">
          {selectedOption.description}
        </p>
      )}

      {/* Flex placeholder */}
      {currentValue === "flex" && (
        <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3">
          <p className="text-center text-[10px] text-[var(--color-muted-foreground)]">
            Flex controls (DSS-31) will appear here
          </p>
        </div>
      )}

      {/* Grid placeholder */}
      {currentValue === "grid" && (
        <div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-muted)]/20 p-3">
          <p className="text-center text-[10px] text-[var(--color-muted-foreground)]">
            Grid controls (DSS-32) will appear here
          </p>
        </div>
      )}

      {/* None warning */}
      {currentValue === "none" && (
        <div className="rounded-lg border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5 p-2">
          <p className="text-center text-[10px] font-medium text-[var(--color-destructive)]">
            This element will not be visible
          </p>
        </div>
      )}

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="text-[10px] text-[var(--color-muted-foreground)]">
          display: {currentValue};
        </code>
      </div>
    </div>
  );
}
